import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { useEvent } from "../lib/event";
import { formatOrdinalDate, formatOrdinalDateTime } from "../lib/timeFormat";
import OptionalCapInput from "../components/OptionalCapInput";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Check, Sparkles, Copy,
  Info, ArrowUp, ArrowDown, X,
} from "lucide-react";

const STEP_LABELS = [
  "Dates", "Times", "Time Slots", "Capacity",
  "Ticket Types", "Add-ons", "Dynamic Pricing", "Sale Window", "Review",
];

const monthNames = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const uid = () => Math.random().toString(36).slice(2, 10);

function MonthCalendar({ year, month, onMonth, selected, onToggle }) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDow = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(d);
  const fmt = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  return (
    <div data-testid="wizard-calendar">
      <div className="flex items-center justify-between mb-3">
        <button className="st-btn-ghost st-btn !py-1.5" onClick={() => onMonth(-1)} data-testid="cal-prev"><ChevronLeft size={16} /></button>
        <div className="text-base font-semibold">{monthNames[month]} {year}</div>
        <button className="st-btn-ghost st-btn !py-1.5" onClick={() => onMonth(1)} data-testid="cal-next"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const dstr = fmt(d);
          const sel = selected.includes(dstr);
          return (
            <button key={i} onClick={() => onToggle(dstr)}
                    data-testid={`cal-day-${dstr}`}
                    className={`h-9 rounded-md border text-sm font-semibold flex items-center justify-center transition-colors ${sel ? "bg-[#dc2626] border-[#dc2626] text-white" : "border-[#2b2b2b] bg-[#0d0d0d] text-[#dadada] hover:border-[#dc2626]"}`}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function EventWizardPage() {
  const nav = useNavigate();
  const { events, setCurrentEventId, refresh: refreshEvents } = useEvent();
  const [mode, setMode] = useState(null); // null | 'fresh' | 'duplicate'
  const [sourceId, setSourceId] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [chooserErr, setChooserErr] = useState("");

  const startFresh = () => setMode("fresh");
  const startDuplicate = async () => {
    if (!sourceId) { setChooserErr("Pick an event to duplicate"); return; }
    setChooserErr(""); setDuplicating(true);
    try {
      const r = await api.post(`/events/${sourceId}/duplicate`, {});
      await refreshEvents();
      setCurrentEventId(r.data.id);
      nav("/dashboard/open-days", { replace: true });
    } catch (e) { setChooserErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setDuplicating(false); }
  };

  if (mode === null) {
    const hasEvents = events.length > 0;
    return (
      <DashboardLayout title="Create new event" subtitle="Pick how you'd like to start.">
        <div className={`grid gap-4 mx-auto ${hasEvents ? "md:grid-cols-2 max-w-4xl" : "max-w-md"}`} data-testid="event-create-chooser">
          <div className="st-card p-8 text-center flex flex-col items-center" data-testid="choose-fresh-card">
            <div className="w-12 h-12 rounded-full bg-[#dc2626] flex items-center justify-center"><Sparkles size={20} /></div>
            <h3 className="text-xl font-bold mt-4">Start Fresh</h3>
            <p className="text-[#bbb] mt-2 text-sm">A guided wizard. Set up dates, time slots, ticket types, pricing and add-ons from scratch.</p>
            <button onClick={startFresh} className="st-btn mt-6" data-testid="choose-fresh">Begin wizard <ChevronRight size={16} /></button>
          </div>

          {hasEvents && (
            <div className="st-card p-8 text-center flex flex-col items-center" data-testid="choose-duplicate-card">
              <div className="w-12 h-12 rounded-full bg-[#1f1f1f] border border-[#2b2b2b] flex items-center justify-center"><Copy size={20} className="text-[#dc2626]" /></div>
              <h3 className="text-xl font-bold mt-4">Duplicate Previous Event</h3>
              <p className="text-[#bbb] mt-2 text-sm">Copy ticket types, add-ons and slot settings from a previous event. Dates and orders are <strong>not</strong> copied.</p>
              <div className="mt-5 w-full text-left">
                <label className="text-xs text-[#8a8a8a]">Source event</label>
                <select className="st-input mt-1" value={sourceId} onChange={(e) => setSourceId(e.target.value)} data-testid="duplicate-source">
                  <option value="">Pick an event…</option>
                  {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              {chooserErr && <div className="text-xs text-[#fca5a5] mt-2">{chooserErr}</div>}
              <button className="st-btn mt-5" onClick={startDuplicate} disabled={duplicating || !sourceId} data-testid="duplicate-confirm">
                {duplicating ? "Duplicating…" : "Duplicate & open"}
              </button>
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  return <WizardSteps onComplete={async (id) => {
    await refreshEvents();
    setCurrentEventId(id);
    nav(`/dashboard/events/${id}/setup-complete`, { replace: true });
  }} onBack={() => setMode(null)} />;
}

function WizardSteps({ onComplete, onBack }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("Halloween 2026");
  const [description, setDescription] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [sameTimes, setSameTimes] = useState(true);
  const [openTime, setOpenTime] = useState("19:00");
  const [closeTime, setCloseTime] = useState("23:00");
  const [perDateTimes, setPerDateTimes] = useState({});
  const [isSlotted, setIsSlotted] = useState(true);
  const [slotInterval, setSlotInterval] = useState(30);
  const [maxCapacity, setMaxCapacity] = useState(100);
  const [ticketTypes, setTicketTypes] = useState([
    { _key: uid(), name: "General Admission", base_price_pence: 1500, max_per_slot: null, tiers: [] },
  ]);
  const [addonsOn, setAddonsOn] = useState(false);
  const [addons, setAddons] = useState([]);
  const [dynamicOn, setDynamicOn] = useState(false);
  const [saleStart, setSaleStart] = useState("");
  const [saleEnd, setSaleEnd] = useState("");

  const toggleMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y);
  };

  const toggleDate = (d) => {
    setSelectedDates((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  };

  const next = () => { setError(""); setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const updateTT = (key, patch) =>
    setTicketTypes((prev) => prev.map((t) => t._key === key ? { ...t, ...patch } : t));
  const removeTT = (key) => setTicketTypes((prev) => prev.filter((t) => t._key !== key));

  const canNext = useMemo(() => {
    if (step === 0) return selectedDates.length > 0 && name.trim().length > 0;
    if (step === 4) return ticketTypes.length > 0 && ticketTypes.every((t) => t.name && t.base_price_pence > 0);
    return true;
  }, [step, selectedDates, name, ticketTypes]);

  const buildDates = () => selectedDates.map((d) => ({
    date: d,
    open_time: sameTimes ? openTime : (perDateTimes[d]?.open || openTime),
    close_time: sameTimes ? closeTime : (perDateTimes[d]?.close || closeTime),
    max_capacity: Number(maxCapacity),
  }));

  const buildTiers = (t) => (dynamicOn ? (t.tiers || []).map((tier, k) => ({
    label: (tier.label || "").trim(),
    tier_price_pence: Number(tier.tier_price_pence || 0),
    expiry_type: tier.expiry_type || "date",
    cutoff_date: tier.expiry_type === "count" ? null : (tier.cutoff_date || null),
    ticket_cap: (tier.cap_enabled && tier.ticket_cap) ? Number(tier.ticket_cap) : null,
    cap_enabled: !!tier.cap_enabled,
    is_active: tier.is_active !== false,
    sort_order: k,
  })) : []);

  // Client-side validation before hitting the API (Change 10).
  const validate = () => {
    if (selectedDates.length === 0) return "Please select at least one open date.";
    if (ticketTypes.length === 0) return "Add at least one ticket type before publishing.";
    if (!ticketTypes.some((t) => Number(t.base_price_pence) > 0)) return "At least one ticket type needs a price greater than £0.";
    if (saleStart && saleEnd && saleStart >= saleEnd) return "The sale window start must be before the end date.";
    if (isSlotted && !slotInterval) return "Please choose a time-slot interval.";
    if (dynamicOn) {
      for (const t of ticketTypes) {
        for (const tier of (t.tiers || [])) {
          if (!(Number(tier.tier_price_pence) > 0)) return `Every price tier on “${t.name}” needs a price.`;
          if ((tier.expiry_type === "date" || tier.expiry_type === "either") && !tier.cutoff_date)
            return `Set the end date for the tier on “${t.name}”.`;
          if ((tier.expiry_type === "count" || tier.expiry_type === "either") && tier.cap_enabled && !tier.ticket_cap)
            return `Set the ticket cap for the tier on “${t.name}”.`;
        }
      }
    }
    return null;
  };

  const submit = async (status = "published") => {
    setError("");
    const msg = validate();
    if (msg) { setError(msg); return; }
    setSubmitting(true);
    try {
      const payload = {
        name, description,
        is_time_slotted: isSlotted,
        slot_interval_minutes: isSlotted ? Number(slotInterval) : null,
        dates: buildDates(),
        ticket_types: ticketTypes.map((t) => ({
          name: t.name,
          base_price_pence: Number(t.base_price_pence),
          max_per_slot: t.max_per_slot ?? null,
          tiers: buildTiers(t),
        })),
        addons: addonsOn ? addons.map((a) => ({ name: a.name, price_pence: Number(a.price_pence) })) : [],
        sale_start_at: saleStart || null,
        sale_end_at: saleEnd || null,
        status,
      };
      const r = await api.post("/events", payload);
      onComplete(r.data.id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(formatErr(detail) || "An unexpected error occurred. Please try again. If the problem persists, contact support.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="New event" subtitle="A few steps to live tickets."
                     actions={<button className="st-btn st-btn-ghost" onClick={onBack} data-testid="wizard-exit">← Choose a different option</button>}>
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8" data-testid="wizard-progress">
        {STEP_LABELS.map((l, i) => (
          <div key={l} className="flex-1">
            <div className={`h-1 rounded ${i <= step ? "bg-[#dc2626]" : "bg-[#1a1a1a]"}`} />
            <div className={`text-[10px] mt-1 uppercase tracking-wider ${i === step ? "text-[#dc2626]" : "text-[#7a7a7a]"}`}>
              {i + 1}. {l}
            </div>
          </div>
        ))}
      </div>

      <div className="st-card p-8 st-fade-up">
        {step === 0 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Pick your dates</h2>
            <p className="text-[#8a8a8a] mb-6">Select all dates you&apos;ll be open. You can edit later.</p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="text-sm text-[#bbb]">Event name</label>
                <input className="st-input mt-1" data-testid="wiz-event-name"
                       value={name} onChange={(e) => setName(e.target.value)} />
                <label className="text-sm text-[#bbb] mt-4 block">Description</label>
                <textarea className="st-input mt-1 min-h-[100px]" data-testid="wiz-event-desc"
                          value={description} onChange={(e) => setDescription(e.target.value)} />
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-[#7a7a7a]">{selectedDates.length} dates selected</div>
                  <div className="text-sm mt-1 text-[#dadada]">{selectedDates.slice(0, 6).map((d) => formatOrdinalDate(d, { weekday: false })).join(", ")}{selectedDates.length > 6 ? ` +${selectedDates.length - 6} more` : ""}</div>
                </div>
              </div>
              <MonthCalendar year={year} month={month} onMonth={toggleMonth}
                             selected={selectedDates} onToggle={toggleDate} />
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Set your times</h2>
            <p className="text-[#8a8a8a] mb-6">Doors open and close.</p>
            <label className="flex items-center gap-3 cursor-pointer mb-6">
              <input type="checkbox" checked={sameTimes} onChange={(e) => setSameTimes(e.target.checked)} data-testid="wiz-same-times" />
              <span className="text-sm">Same times for every date</span>
            </label>
            {sameTimes ? (
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <div>
                  <label className="text-sm text-[#bbb]">Open</label>
                  <input type="time" className="st-input mt-1" data-testid="wiz-open" value={openTime} onChange={(e) => setOpenTime(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm text-[#bbb]">Close</label>
                  <input type="time" className="st-input mt-1" data-testid="wiz-close" value={closeTime} onChange={(e) => setCloseTime(e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {selectedDates.map((d) => (
                  <div key={d} className="grid grid-cols-3 gap-3 items-center">
                    <div className="text-sm">{formatOrdinalDate(d, { weekday: false })}</div>
                    <input type="time" className="st-input"
                           value={perDateTimes[d]?.open || openTime}
                           onChange={(e) => setPerDateTimes({ ...perDateTimes, [d]: { ...(perDateTimes[d] || {}), open: e.target.value } })} />
                    <input type="time" className="st-input"
                           value={perDateTimes[d]?.close || closeTime}
                           onChange={(e) => setPerDateTimes({ ...perDateTimes, [d]: { ...(perDateTimes[d] || {}), close: e.target.value } })} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Time slotted?</h2>
            <p className="text-[#8a8a8a] mb-6">Split each night into entry windows.</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={isSlotted} onChange={(e) => setIsSlotted(e.target.checked)} data-testid="wiz-slotted-toggle" />
              <span>Yes, use time slots</span>
            </label>
            {isSlotted && (
              <div className="mt-6 max-w-md">
                <label className="text-sm text-[#bbb]">Interval (minutes)</label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[15, 20, 30, 45, 60, 90].map((v) => (
                    <button key={v} onClick={() => setSlotInterval(v)}
                            data-testid={`wiz-interval-${v}`}
                            className={`px-4 py-2 rounded-lg border ${slotInterval == v ? "border-[#dc2626] bg-[#dc2626] text-white" : "border-[#2b2b2b]"}`}>
                      {v}
                    </button>
                  ))}
                </div>
                <div className="text-xs text-[#8a8a8a] mt-3">Preview: {openTime}, {addMinutes(openTime, slotInterval)}, {addMinutes(openTime, slotInterval * 2)} …</div>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Capacity</h2>
            <p className="text-[#8a8a8a] mb-6">Max attendees {isSlotted ? "per time slot" : "per date"}.</p>
            <input type="number" className="st-input max-w-[200px]" min="1"
                   value={maxCapacity} onChange={(e) => setMaxCapacity(e.target.value)} data-testid="wiz-capacity" />
            <p className="text-xs text-[#8a8a8a] mt-3">You can set per ticket-type limits on the next step.</p>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Ticket types</h2>
            <p className="text-[#8a8a8a] mb-6">At least one ticket type required.</p>
            <div className="space-y-3" data-testid="wiz-ticket-list">
              {ticketTypes.map((t, i) => (
                <TicketTypeRow key={t._key} t={t} index={i}
                               onChange={(patch) => updateTT(t._key, patch)}
                               onRemove={() => removeTT(t._key)} />
              ))}
            </div>
            <button className="st-btn st-btn-ghost mt-4" data-testid="wiz-add-tt"
                    onClick={() => setTicketTypes([...ticketTypes, { _key: uid(), name: "", base_price_pence: 1000, max_per_slot: null, tiers: [] }])}>
              <Plus size={14} /> Add ticket type
            </button>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Add-ons</h2>
            <p className="text-[#8a8a8a] mb-6">Boost revenue with optional upsells.</p>
            <label className="flex items-center gap-3 cursor-pointer mb-4">
              <input type="checkbox" checked={addonsOn} onChange={(e) => setAddonsOn(e.target.checked)} data-testid="wiz-addons-toggle" />
              <span>Offer add-on upgrades</span>
            </label>
            {addonsOn && (
              <>
                <div className="space-y-3">
                  {addons.map((a, i) => (
                    <div key={i} className="st-card p-4 flex items-end gap-3">
                      <div className="flex-1">
                        <label className="text-xs text-[#8a8a8a]">Name</label>
                        <input className="st-input mt-1" value={a.name}
                               onChange={(e) => setAddons(addons.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                      </div>
                      <div className="w-32">
                        <label className="text-xs text-[#8a8a8a]">Price (£)</label>
                        <input type="number" step="0.01" className="st-input mt-1"
                               value={(a.price_pence / 100).toFixed(2)}
                               onChange={(e) => setAddons(addons.map((x, j) => j === i ? { ...x, price_pence: Math.round(parseFloat(e.target.value || 0) * 100) } : x))} />
                      </div>
                      <button onClick={() => setAddons(addons.filter((_, j) => j !== i))} className="text-[#dc2626] p-2"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <button className="st-btn st-btn-ghost mt-4" data-testid="wiz-add-addon"
                        onClick={() => setAddons([...addons, { name: "", price_pence: 500 }])}>
                  <Plus size={14} /> Add an add-on
                </button>
              </>
            )}
          </div>
        )}

        {step === 6 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Early Bird &amp; Dynamic Pricing</h2>
            <p className="text-[#8a8a8a] mb-5 max-w-2xl">Create price tiers that automatically change the ticket price based on a cutoff date or number of tickets sold. For example: £10 before 1st October, £12 before 15th October, then £15 full price.</p>
            <label className="flex items-center gap-3 cursor-pointer mb-4">
              <input type="checkbox" checked={dynamicOn} onChange={(e) => setDynamicOn(e.target.checked)} data-testid="wiz-dynamic-toggle" />
              <span>Enable dynamic pricing</span>
            </label>
            {dynamicOn && ticketTypes.map((t, i) => (
              <TicketTierEditor key={t._key} t={t} index={i} onChange={(patch) => updateTT(t._key, patch)} />
            ))}
          </div>
        )}

        {step === 7 && (
          <div>
            <h2 className="text-xl font-bold mb-1">Sale window</h2>
            <p className="text-[#8a8a8a] mb-6">When tickets are available for purchase.</p>
            <div className="grid grid-cols-2 gap-4 max-w-xl">
              <div>
                <label className="text-sm text-[#bbb]">Sale starts</label>
                <input type="datetime-local" className="st-input mt-1" value={saleStart} onChange={(e) => setSaleStart(e.target.value)} data-testid="wiz-sale-start" />
              </div>
              <div>
                <label className="text-sm text-[#bbb]">Sale ends</label>
                <input type="datetime-local" className="st-input mt-1" value={saleEnd} onChange={(e) => setSaleEnd(e.target.value)} data-testid="wiz-sale-end" />
              </div>
            </div>
          </div>
        )}

        {step === 8 && (
          <div>
            <h2 className="text-xl font-bold mb-2">Review &amp; publish</h2>
            <p className="text-[#8a8a8a] mb-6">Looks good? Publish to go live.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <ReviewBox label="Event">{name}<br /><span className="text-xs text-[#8a8a8a]">{description || "—"}</span></ReviewBox>
              <ReviewBox label="Dates">
                {selectedDates.length} selected
                <div className="text-xs text-[#8a8a8a] mt-1">{selectedDates.slice(0, 4).map((d) => formatOrdinalDate(d, { weekday: false })).join(" · ")}{selectedDates.length > 4 ? " …" : ""}</div>
              </ReviewBox>
              <ReviewBox label="Slots">{isSlotted ? `Every ${slotInterval} min` : "Not slotted"}</ReviewBox>
              <ReviewBox label="Capacity">{maxCapacity} per {isSlotted ? "slot" : "date"}</ReviewBox>
              <ReviewBox label="Ticket types">{ticketTypes.map((t) => `${t.name} ${poundsFromPence(t.base_price_pence)}`).join(", ")}</ReviewBox>
              <ReviewBox label="Add-ons">{addonsOn && addons.length ? addons.map((a) => a.name).join(", ") : "—"}</ReviewBox>
              <ReviewBox label="Dynamic pricing">
                {dynamicOn ? (
                  <div className="space-y-1">
                    {ticketTypes.filter((t) => (t.tiers || []).length).map((t) => (
                      <div key={t._key} className="text-xs">
                        <span className="text-[#dadada] font-medium">{t.name}:</span>{" "}
                        {(t.tiers || []).map((tier, k) => (
                          <span key={k} className="text-[#8a8a8a]">
                            {poundsFromPence(tier.tier_price_pence)} {tierConditionLabel(tier)}{k < t.tiers.length - 1 ? "; " : ""}
                          </span>
                        ))}
                      </div>
                    ))}
                    {!ticketTypes.some((t) => (t.tiers || []).length) && <span className="text-[#8a8a8a]">On — no tiers added</span>}
                  </div>
                ) : "Off"}
              </ReviewBox>
              <ReviewBox label="Sale window">
                {saleStart ? formatOrdinalDateTime(saleStart) : "Now"} → {saleEnd ? formatOrdinalDateTime(saleEnd) : "No end"}
              </ReviewBox>
            </div>
            {error && (
              <div className="text-sm text-[#fca5a5] bg-[#2a1212] border border-[#5b1919] rounded-lg p-3 mt-4 flex items-start justify-between gap-3" data-testid="wiz-error">
                <span>{error}</span>
                <button onClick={() => setError("")} className="text-[#fca5a5] hover:text-white shrink-0" data-testid="wiz-error-dismiss"><X size={15} /></button>
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <button className="st-btn st-btn-ghost" onClick={() => submit("draft")} disabled={submitting} data-testid="wiz-save-draft">{submitting ? "Saving…" : "Save as draft"}</button>
              <button className="st-btn" onClick={() => submit("published")} disabled={submitting} data-testid="wiz-publish"><Check size={16} /> {submitting ? "Publishing…" : "Publish"}</button>
            </div>
          </div>
        )}

        {/* Footer nav */}
        {step < 8 && (
          <div className="flex justify-between mt-8 pt-6 border-t border-[#1a1a1a]">
            <button className="st-btn st-btn-ghost" onClick={back} disabled={step === 0} data-testid="wiz-back"><ChevronLeft size={16} /> Back</button>
            <button className="st-btn" onClick={next} disabled={!canNext} data-testid="wiz-next">Next <ChevronRight size={16} /></button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─────────────── Ticket type row (Change 7 — stable key + focus-safe price input) ───────────────
function TicketTypeRow({ t, index, onChange, onRemove }) {
  const [priceStr, setPriceStr] = useState((Number(t.base_price_pence || 0) / 100).toFixed(2));

  const onPrice = (v) => {
    setPriceStr(v);
    const pence = Math.round(parseFloat(v || 0) * 100);
    onChange({ base_price_pence: isNaN(pence) ? 0 : pence });
  };

  return (
    <div className="st-card p-4 flex items-end gap-3">
      <div className="flex-1">
        <label className="text-xs text-[#8a8a8a]">Name</label>
        <input className="st-input mt-1" value={t.name} data-testid={`wiz-tt-name-${index}`}
               onChange={(e) => onChange({ name: e.target.value })} />
      </div>
      <div className="w-32">
        <label className="text-xs text-[#8a8a8a]">Price (£)</label>
        <input type="number" step="0.01" min="0" className="st-input mt-1"
               value={priceStr} data-testid={`wiz-tt-price-${index}`}
               onChange={(e) => onPrice(e.target.value)} />
      </div>
      <div className="w-32">
        <label className="text-xs text-[#8a8a8a] flex items-center gap-1">
          Max/slot
          <span className="relative inline-flex group">
            <Info size={12} className="text-[#7a7a7a] cursor-help" data-testid={`wiz-tt-maxinfo-${index}`} />
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 hidden group-hover:block w-52 bg-[#1a1a1a] border border-[#2b2b2b] text-[11px] text-[#ccc] rounded-md p-2 z-50 normal-case tracking-normal leading-snug shadow-lg">
              Limits how many of this ticket type can be purchased per time slot. Leave blank for no limit.
            </span>
          </span>
        </label>
        <div className="mt-1">
          <OptionalCapInput value={t.max_per_slot ?? null} onChange={(v) => onChange({ max_per_slot: v })} testid={`wiz-tt-maxslot-${index}`} />
        </div>
      </div>
      <button onClick={onRemove} className="text-[#dc2626] p-2" data-testid={`wiz-tt-del-${index}`}><Trash2 size={16} /></button>
    </div>
  );
}

function tierConditionLabel(tier) {
  const parts = [];
  if ((tier.expiry_type === "date" || tier.expiry_type === "either") && tier.cutoff_date)
    parts.push(`until ${formatOrdinalDate(tier.cutoff_date, { weekday: false })}`);
  if ((tier.expiry_type === "count" || tier.expiry_type === "either") && tier.cap_enabled && tier.ticket_cap)
    parts.push(`first ${tier.ticket_cap} tickets`);
  return parts.length ? `(${parts.join(" / ")})` : "";
}

// ─────────────── Dynamic pricing editor per ticket type (Change 8) ───────────────
function TicketTierEditor({ t, index, onChange }) {
  const tiers = t.tiers || [];
  const setTiers = (nextTiers) => onChange({ tiers: nextTiers });

  const addTier = () => setTiers([...tiers, {
    _key: uid(), label: "", tier_price_pence: Math.max(0, Number(t.base_price_pence) - 200),
    expiry_type: "date", cutoff_date: "", ticket_cap: null, cap_enabled: false, is_active: true,
  }]);
  const updateTier = (key, patch) => setTiers(tiers.map((x) => x._key === key ? { ...x, ...patch } : x));
  const removeTier = (key) => setTiers(tiers.filter((x) => x._key !== key));
  const moveTier = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= tiers.length) return;
    const copy = [...tiers];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    setTiers(copy);
  };

  return (
    <div className="st-card p-4 mt-3" data-testid={`wiz-tier-card-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold">{t.name} <span className="text-xs text-[#8a8a8a]">Base {poundsFromPence(t.base_price_pence)}</span></div>
        <button className="st-btn st-btn-ghost text-xs" onClick={addTier} data-testid={`wiz-add-tier-${index}`}><Plus size={14} /> Add tier</button>
      </div>

      {tiers.length === 0 && <p className="text-xs text-[#7a7a7a]">No tiers yet — the base price applies to everyone.</p>}

      <div className="space-y-3">
        {tiers.map((tier, k) => (
          <TierRow key={tier._key} tier={tier} ttIndex={index} idx={k} count={tiers.length}
                   onChange={(patch) => updateTier(tier._key, patch)}
                   onRemove={() => removeTier(tier._key)}
                   onMove={(dir) => moveTier(k, dir)} />
        ))}
      </div>

      {tiers.length > 0 && (
        <div className="text-xs text-[#8a8a8a] mt-3 pt-3 border-t border-[#1a1a1a]">
          After all tiers expire: <span className="text-[#dadada] font-medium">{poundsFromPence(t.base_price_pence)}</span> (your standard price)
        </div>
      )}
    </div>
  );
}

function TierRow({ tier, ttIndex, idx, count, onChange, onRemove, onMove }) {
  const [priceStr, setPriceStr] = useState(tier.tier_price_pence != null ? (Number(tier.tier_price_pence) / 100).toFixed(2) : "");
  const et = tier.expiry_type || "date";
  const showDate = et === "date" || et === "either";
  const showCount = et === "count" || et === "either";

  const onPrice = (v) => {
    setPriceStr(v);
    const pence = Math.round(parseFloat(v || 0) * 100);
    onChange({ tier_price_pence: isNaN(pence) ? 0 : pence });
  };
  const onCap = (raw) => {
    if (raw === "") return onChange({ ticket_cap: null });
    let n = parseInt(raw, 10);
    if (isNaN(n) || n < 1) n = 1;
    onChange({ ticket_cap: n });
  };

  return (
    <div className={`rounded-lg border p-3 ${tier.is_active === false ? "border-[#2b2b2b] bg-[#0c0c0c] opacity-70" : "border-[#262626] bg-[#0e0e0e]"}`} data-testid={`wiz-tier-${ttIndex}-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-1 pt-5">
          <button onClick={() => onMove(-1)} disabled={idx === 0} className="text-[#7a7a7a] hover:text-white disabled:opacity-30" data-testid={`wiz-tier-up-${ttIndex}-${idx}`}><ArrowUp size={14} /></button>
          <button onClick={() => onMove(1)} disabled={idx === count - 1} className="text-[#7a7a7a] hover:text-white disabled:opacity-30" data-testid={`wiz-tier-down-${ttIndex}-${idx}`}><ArrowDown size={14} /></button>
        </div>

        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#8a8a8a]">Tier name</label>
            <input className="st-input mt-1" placeholder="e.g. Early Bird, Last Minute" value={tier.label || ""}
                   data-testid={`wiz-tier-label-${ttIndex}-${idx}`}
                   onChange={(e) => onChange({ label: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#8a8a8a]">Tier price (£)</label>
            <input type="number" step="0.01" min="0" className="st-input mt-1" value={priceStr}
                   data-testid={`wiz-tier-price-${ttIndex}-${idx}`}
                   onChange={(e) => onPrice(e.target.value)} />
            <p className="text-[10px] text-[#7a7a7a] mt-1">The price charged during this tier.</p>
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-[#8a8a8a]">This tier ends when:</label>
            <div className="flex flex-wrap gap-3 mt-1.5">
              {[
                { v: "date", l: "A date is reached" },
                { v: "count", l: "Tickets sold reaches a limit" },
                { v: "either", l: "Either (whichever comes first)" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name={`et-${ttIndex}-${idx}`} checked={et === opt.v}
                         data-testid={`wiz-tier-et-${opt.v}-${ttIndex}-${idx}`}
                         onChange={() => onChange({ expiry_type: opt.v })} />
                  <span>{opt.l}</span>
                </label>
              ))}
            </div>
          </div>

          {showDate && (
            <div>
              <label className="text-xs text-[#8a8a8a]">Tier ends on</label>
              <input type="date" className="st-input mt-1" value={tier.cutoff_date || ""}
                     data-testid={`wiz-tier-cutoff-${ttIndex}-${idx}`}
                     onChange={(e) => onChange({ cutoff_date: e.target.value })} />
              <p className="text-[10px] text-[#7a7a7a] mt-1">After this date, the next tier price applies.</p>
            </div>
          )}

          {showCount && (
            <div>
              <label className="flex items-center gap-2 text-xs text-[#8a8a8a] cursor-pointer">
                <input type="checkbox" checked={!!tier.cap_enabled}
                       data-testid={`wiz-tier-capenabled-${ttIndex}-${idx}`}
                       onChange={(e) => onChange({ cap_enabled: e.target.checked })} />
                Enable ticket cap for this tier
              </label>
              {tier.cap_enabled && (
                <>
                  <input type="number" min="1" className="st-input mt-1.5" value={tier.ticket_cap ?? ""} placeholder="e.g. 100"
                         data-testid={`wiz-tier-cap-${ttIndex}-${idx}`}
                         onChange={(e) => onCap(e.target.value)} />
                  <p className="text-[10px] text-[#7a7a7a] mt-1">After this many tickets are sold, the next tier applies.</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 pt-5">
          <label className="flex items-center gap-1.5 text-[11px] text-[#8a8a8a] cursor-pointer">
            <input type="checkbox" checked={tier.is_active !== false}
                   data-testid={`wiz-tier-active-${ttIndex}-${idx}`}
                   onChange={(e) => onChange({ is_active: e.target.checked })} />
            Active
          </label>
          <button onClick={onRemove} className="text-[#dc2626]" data-testid={`wiz-tier-del-${ttIndex}-${idx}`}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

function ReviewBox({ label, children }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function addMinutes(time, mins) {
  const [h, m] = time.split(":").map(Number);
  const d = new Date(); d.setHours(h, m + mins, 0, 0);
  return d.toTimeString().slice(0, 5);
}
